import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import { 
  Key, Bot, Eye, EyeOff, CheckCircle2, AlertCircle, 
  ExternalLink, Settings, Users, Shield
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_ROLES = [
  { value: 'builder_admin', label: 'Builder Admin', icon: Shield },
  { value: 'sales_team', label: 'Sales Team', icon: Users },
  { value: 'partner', label: 'Channel Partners', icon: Users },
  { value: 'sub_user', label: 'Sub Users', icon: Users },
];

export const AdminAISettingsDialog = ({ open, onClose, onSettingsChanged }) => {
  const { user, hasRole } = useAuth();
  const [settings, setSettings] = useState({
    openai_api_key: '',
    ai_enabled: false,
    enabled_roles: [],
    features: {
      voice_transcription: true,
      smart_suggestions: true,
      call_summaries: true,
      next_action_recommendations: true
    }
  });
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [canConfigure, setCanConfigure] = useState(false);

  const isAdmin = hasRole(['super_admin', 'builder_admin']);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCanConfigure(data.can_configure);
        setSettings({
          openai_api_key: data.openai_api_key || '',
          ai_enabled: data.ai_enabled,
          enabled_roles: data.enabled_roles || [],
          features: data.features || {
            voice_transcription: true,
            smart_suggestions: true,
            call_summaries: true,
            next_action_recommendations: true
          }
        });
      }
    } catch (error) {
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestKey = async () => {
    if (!settings.openai_api_key.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: { 'Authorization': `Bearer ${settings.openai_api_key}` },
      });

      if (response.ok) {
        setTestResult({ success: true, message: 'API key is valid!' });
        toast.success('API key verified successfully');
      } else {
        const error = await response.json();
        setTestResult({ 
          success: false, 
          message: error.error?.message || 'Invalid API key' 
        });
        toast.error('Invalid API key');
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: 'Failed to test API key. Check your internet connection.' 
      });
      toast.error('Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/ai-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          openai_api_key: settings.openai_api_key,
          ai_enabled: settings.ai_enabled,
          enabled_roles: settings.enabled_roles,
          features: settings.features
        }),
      });

      if (response.ok) {
        toast.success('AI settings saved successfully');
        onSettingsChanged && onSettingsChanged();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setSettings(prev => ({
      ...prev,
      enabled_roles: prev.enabled_roles.includes(role)
        ? prev.enabled_roles.filter(r => r !== role)
        : [...prev.enabled_roles, role]
    }));
  };

  const toggleFeature = (feature) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const maskKey = (key) => {
    if (!key || key.length < 10) return key;
    return key.slice(0, 7) + '...' + key.slice(-4);
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Non-admin view
  if (!canConfigure) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-violet-600" />
              AI Assistant
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {settings.ai_enabled ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  AI features are enabled for your account. You can use voice transcription and smart suggestions in the Call Assistant.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  AI features are not enabled for your role. Contact your administrator to enable AI assistance.
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <p className="text-sm font-medium">Available Features:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  {settings.ai_enabled ? (
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                  Voice-to-text call disposition
                </li>
                <li className="flex items-center gap-2">
                  {settings.ai_enabled ? (
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                  Smart stage change suggestions
                </li>
                <li className="flex items-center gap-2">
                  {settings.ai_enabled ? (
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                  Automated call summaries
                </li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Admin configuration view
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-violet-600" />
            AI Settings (Admin)
          </DialogTitle>
          <DialogDescription>
            Configure AI features for your organization. Add your OpenAI API key and choose which roles can use AI.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Master Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-violet-50 rounded-lg border border-violet-200">
            <div className="flex items-center gap-3">
              <Bot className="h-5 w-5 text-violet-600" />
              <div>
                <p className="font-medium">Enable AI Assistant</p>
                <p className="text-sm text-muted-foreground">
                  Turn on AI features for your organization
                </p>
              </div>
            </div>
            <Switch
              checked={settings.ai_enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_enabled: checked }))}
            />
          </div>

          {settings.ai_enabled && (
            <>
              {/* API Key Input */}
              <div className="space-y-2">
                <Label htmlFor="openai-key" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  OpenAI API Key
                </Label>
                <div className="relative">
                  <Input
                    id="openai-key"
                    type={showKey ? 'text' : 'password'}
                    value={settings.openai_api_key}
                    onChange={(e) => setSettings(prev => ({ ...prev, openai_api_key: e.target.value }))}
                    placeholder="sk-..."
                    className="pr-20"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Your key is stored securely and used for all AI features.
                  </p>
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Get key <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>

              {/* Test Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={handleTestKey}
                disabled={testing || !settings.openai_api_key.trim()}
              >
                {testing ? 'Testing...' : 'Test API Key'}
              </Button>

              {testResult && (
                <Alert variant={testResult.success ? 'default' : 'destructive'}>
                  {testResult.success ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  <AlertDescription>{testResult.message}</AlertDescription>
                </Alert>
              )}

              {/* Role Access Control */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Enable AI for Roles
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_ROLES.map((role) => (
                    <Card 
                      key={role.value}
                      className={`cursor-pointer transition-all ${
                        settings.enabled_roles.includes(role.value)
                          ? 'border-violet-400 bg-violet-50'
                          : 'hover:border-gray-300'
                      }`}
                      onClick={() => toggleRole(role.value)}
                    >
                      <CardContent className="p-3 flex items-center gap-2">
                        <Checkbox
                          checked={settings.enabled_roles.includes(role.value)}
                          onCheckedChange={() => toggleRole(role.value)}
                        />
                        <role.icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{role.label}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Super Admin always has access. Select additional roles that can use AI features.
                </p>
              </div>

              {/* Features Toggle */}
              <div className="space-y-3">
                <Label>AI Features</Label>
                <div className="space-y-2">
                  {[
                    { key: 'voice_transcription', label: 'Voice Transcription', desc: 'Convert voice memos to text' },
                    { key: 'smart_suggestions', label: 'Smart Suggestions', desc: 'AI-powered stage recommendations' },
                    { key: 'call_summaries', label: 'Call Summaries', desc: 'Auto-generate call notes' },
                    { key: 'next_action_recommendations', label: 'Action Recommendations', desc: 'Suggest next steps' },
                  ].map((feature) => (
                    <div key={feature.key} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium">{feature.label}</p>
                        <p className="text-xs text-muted-foreground">{feature.desc}</p>
                      </div>
                      <Switch
                        checked={settings.features[feature.key]}
                        onCheckedChange={() => toggleFeature(feature.key)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAISettingsDialog;
